import { useMemo } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  LETRAS_MINIMAS_TOLERANCIA,
  UMBRAL_POR_DEFECTO,
  accionPara,
  difiereEnUnaLetra,
  esCombinacion,
  palabrasSonido,
  plano,
  quedanPocas,
  sevendePorUnidad,
  sonido,
  tipoEfectivo,
  type AccionPrincipio,
  type Coincidencia,
  type TipoProducto,
  type UmbralBotica,
} from "@huayruro/shared";
import type { HuayruroDB, ProductoLocal, PresentacionLocal, PrecioLocal, CodigoLocal } from "./db-local";
import { META_UMBRAL, sinTildes } from "./sync";
import type { ProductoVenta } from "./tipos";

// Catálogo de venta desde Dexie (§11.1: useCatalogo REESCRITO — lee de la cache, no de la red →
// funciona offline). Carga el catálogo entero en memoria (cientos–pocos miles de SKU = trivial) y
// resuelve búsqueda + GTIN sin tocar IndexedDB por tecla. Δ1: cada producto expone sus
// presentaciones con su precio, para que el operador elija unidad/blíster/caja.

export type PresentacionVenta = {
  presentacion_id: string;
  presentacion_nombre: string;
  factor: number;
  precio_sin_igv_dm: number;
  precio_total_dm: number;
  es_base: boolean;
};

export type ResultadoBusqueda = {
  producto_id: string;
  nombre: string;
  presentacion_texto: string | null;
  laboratorio: string | null;
  principio_activo: string | null;
  categoria: string | null;
  requiere_receta: boolean;
  stock_cache: number | null;
  presentaciones: PresentacionVenta[]; // solo las que tienen precio vigente
  /** Por qué salió (Kevin, 15-oct-2026): «nombre» = lo buscado; «similar» = otra marca con el mismo
   *  principio activo; «generico» = el genérico de ese principio activo. En ese orden. */
  grupo?: GrupoBusqueda;
  // Buscador del Mostrador (Kevin, 16-oct-2026).
  /** marca / generico / aseo / galenico: pinta el fondo y el color del nombre. */
  tipo: TipoProducto;
  /** Qué hace su principio activo (de Panel › Copiloto); null si nadie lo escribió todavía. */
  accion: string | null;
  /** Dónde está en esta botica. */
  ubicacion: string | null;
  /** El triángulo amarillo: quedan menos que el umbral de esta botica (o que el mínimo del producto). */
  quedan_pocas: boolean;
  /** Se cuenta por unidad suelta (tabletas…) o por envase (jarabes…): dice «und.» o «env.». */
  por_unidad: boolean;
  /** Cómo coincidió con lo tipeado, si no fue tal cual: «sonido» o «letra» (una letra de diferencia). */
  coincidencia?: Coincidencia;
};

export type GrupoBusqueda = "nombre" | "similar" | "generico";

export type Cache = {
  productos: ProductoLocal[];
  presPorProducto: Map<string, PresentacionLocal[]>;
  precioPorClave: Map<string, PrecioLocal>; // `${producto_id}:${presentacion_id}`
  gtin: Map<string, CodigoLocal>;
  stock: Map<string, number>;
  nombreNorm: Map<string, string>; // id → nombre sin tildes (búsqueda)
  paNorm: Map<string, string>; // id → principio activo sin tildes ni espacios de más ("" si no tiene)
  // Tolerancia (16-oct-2026), calculada UNA vez al armar la copia y no por tecla:
  nombrePlano: Map<string, string>; // sin tildes ni espacios: «para cetamol» encuentra «PARACETAMOL»
  nombreSonido: Map<string, string>; // cómo suena: «ivuprofeno» encuentra «IBUPROFENO»
  nombrePalabras: Map<string, string[]>; // cada palabra como suena, para la tolerancia de una letra
  paSonido: Map<string, string>;
  minimo: Map<string, number>;
  ubicacion: Map<string, string | null>;
  acciones: AccionPrincipio[];
  umbral: UmbralBotica;
};

const normalizarPa = (pa: string | null): string => sinTildes((pa ?? "").trim()).replace(/\s+/g, " ");

// Genérico o marca: lo dice `tipoEfectivo` (@huayruro/shared): lo que marcó la botica o, si no marcó nada,
// la regla del nombre («PARACETAMOL 500 MG» es genérico; «PANADOL», marca).

function armarPresentaciones(
  producto: ProductoLocal,
  cache: Cache,
): PresentacionVenta[] {
  const pres = (cache.presPorProducto.get(producto.id) ?? [])
    .slice()
    .sort((a, b) => a.factor_unidades - b.factor_unidades);
  const out: PresentacionVenta[] = [];
  for (const p of pres) {
    const precio = cache.precioPorClave.get(`${producto.id}:${p.id}`);
    if (!precio) continue; // sin precio vigente no se puede vender
    out.push({
      presentacion_id: p.id,
      presentacion_nombre: p.nombre,
      factor: p.factor_unidades,
      precio_sin_igv_dm: precio.precio_sin_igv_dm,
      precio_total_dm: precio.precio_total_dm,
      es_base: p.es_base === 1,
    });
  }
  return out;
}

function aResultado(producto: ProductoLocal, cache: Cache): ResultadoBusqueda {
  const stock = cache.stock.get(producto.id) ?? null;
  const factores = (cache.presPorProducto.get(producto.id) ?? []).map((p) => p.factor_unidades);
  const porUnidad = sevendePorUnidad(producto.categoria, factores);
  return {
    tipo: tipoEfectivo(producto.tipo, producto.nombre, producto.principio_activo),
    accion: accionPara(cache.acciones, producto.principio_activo),
    ubicacion: cache.ubicacion.get(producto.id) ?? null,
    quedan_pocas: quedanPocas(stock, cache.minimo.get(producto.id) ?? 0, porUnidad, cache.umbral),
    por_unidad: porUnidad,
    producto_id: producto.id,
    nombre: producto.nombre,
    presentacion_texto: producto.presentacion,
    laboratorio: producto.laboratorio,
    principio_activo: producto.principio_activo,
    categoria: producto.categoria,
    // Viene en la copia local desde el 26-oct-2026; el servidor igual lo vuelve a mirar al cobrar.
    requiere_receta: producto.requiere_receta === 1,
    stock_cache: cache.stock.get(producto.id) ?? null,
    presentaciones: armarPresentaciones(producto, cache),
  };
}

// Construye un ProductoVenta (para el carrito) desde un resultado + la presentación elegida.
export function aProductoVenta(r: ResultadoBusqueda, pres: PresentacionVenta, gtin: string | null): ProductoVenta {
  return {
    producto_id: r.producto_id,
    nombre: r.nombre,
    presentacion_texto: r.presentacion_texto,
    laboratorio: r.laboratorio,
    principio_activo: r.principio_activo,
    categoria: r.categoria,
    requiere_receta: r.requiere_receta,
    presentacion_id: pres.presentacion_id,
    presentacion_nombre: pres.presentacion_nombre,
    factor: pres.factor,
    precio_sin_igv_dm: pres.precio_sin_igv_dm,
    precio_total_dm: pres.precio_total_dm,
    stock_cache: r.stock_cache,
    gtin,
  };
}

type DatosCatalogo = {
  productos: ProductoLocal[];
  presentaciones: PresentacionLocal[];
  precios: PrecioLocal[];
  codigos: CodigoLocal[];
  stock: { producto_id: string; stock_unidades: number; stock_minimo?: number; ubicacion?: string | null }[];
  acciones?: AccionPrincipio[];
  umbral?: UmbralBotica | null;
};

export function armarCache(datos: DatosCatalogo): Cache {
  const presPorProducto = new Map<string, PresentacionLocal[]>();
  for (const p of datos.presentaciones) {
    const arr = presPorProducto.get(p.producto_id);
    if (arr) arr.push(p);
    else presPorProducto.set(p.producto_id, [p]);
  }
  const precioPorClave = new Map<string, PrecioLocal>();
  for (const pr of datos.precios) precioPorClave.set(`${pr.producto_id}:${pr.presentacion_id}`, pr);
  const gtin = new Map<string, CodigoLocal>();
  for (const c of datos.codigos) gtin.set(c.gtin, c);
  const stock = new Map<string, number>();
  const minimo = new Map<string, number>();
  const ubicacion = new Map<string, string | null>();
  for (const s of datos.stock) {
    stock.set(s.producto_id, s.stock_unidades);
    minimo.set(s.producto_id, s.stock_minimo ?? 0);
    ubicacion.set(s.producto_id, s.ubicacion ?? null);
  }
  const nombreNorm = new Map<string, string>();
  const nombrePlano = new Map<string, string>();
  const nombreSonido = new Map<string, string>();
  const nombrePalabras = new Map<string, string[]>();
  const paNorm = new Map<string, string>();
  const paSonido = new Map<string, string>();
  for (const p of datos.productos) {
    nombreNorm.set(p.id, sinTildes(p.nombre));
    nombrePlano.set(p.id, plano(p.nombre));
    nombreSonido.set(p.id, sonido(p.nombre));
    nombrePalabras.set(p.id, palabrasSonido(p.nombre));
    const pa = normalizarPa(p.principio_activo);
    paNorm.set(p.id, pa);
    paSonido.set(p.id, sonido(pa));
  }
  return {
    productos: datos.productos, presPorProducto, precioPorClave, gtin, stock, nombreNorm, paNorm,
    nombrePlano, nombreSonido, nombrePalabras, paSonido, minimo, ubicacion,
    acciones: datos.acciones ?? [], umbral: datos.umbral ?? UMBRAL_POR_DEFECTO,
  };
}

// ¿Alguna palabra (ya como suena) difiere en UNA letra del comienzo de lo tipeado? Solo desde 5 letras.
function unaLetra(palabras: string[], qs: string): boolean {
  if (qs.length < LETRAS_MINIMAS_TOLERANCIA) return false;
  for (const p of palabras) {
    for (const largo of [qs.length - 1, qs.length, qs.length + 1]) {
      if (largo > 0 && difiereEnUnaLetra(p.slice(0, largo), qs)) return true;
    }
  }
  return false;
}

// ¿Se puede vender en esta botica? = alguna de sus presentaciones tiene precio en la copia local.
function tienePrecio(producto: ProductoLocal, cache: Cache): boolean {
  return (cache.presPorProducto.get(producto.id) ?? []).some((pr) => cache.precioPorClave.has(`${producto.id}:${pr.id}`));
}

// MOS-8 (Kevin, 30-sep): el catálogo es de toda la cadena y los precios son de cada botica. Antes se
// cortaba en `limite` y RECIÉN después se escondía lo que no tiene precio aquí: en Chazuta los primeros
// 50 eran de VES y lo propio no salía (medido: 10 de 10 ajenos, 0 de 10 propios). Ahora se filtra antes.
// Búsqueda por PRINCIPIO ACTIVO (Kevin, 15-oct-2026, probando en VES): lo que se busca no es solo el nombre.
// Tres grupos, en este orden: (1) lo que coincide por nombre o código; (2) otras marcas con el mismo
// principio activo; (3) los genéricos de ese principio activo. Los principios activos salen de lo
// encontrado por nombre («panadol» → paracetamol) y, desde 3 letras, de lo tipeado («paracetamol» trae
// también las marcas aunque su nombre no lo diga).
export const LIMITE_POR_GRUPO = { nombre: 30, similar: 20, generico: 20 } as const;

// TOLERANCIA (Kevin, 16-oct-2026): «no centrarse en la exactitud de los caracteres, de los espacios, de las
// tildes … hay clientes que vienen pronunciando un nombre muy similar». Por nombre se busca en tres pasadas,
// cada una solo si la anterior no juntó al menos 10: tal cual (sin tildes ni espacios), por cómo suena y,
// desde 5 letras, con una letra de diferencia. Lo exacto siempre va primero.
export const MINIMO_ANTES_DE_TOLERAR = 10;

export function buscarEnCache(cache: Cache, q: string, limite = 50): ResultadoBusqueda[] {
  const termino = sinTildes(q.trim());
  const vendible = (p: ProductoLocal) => tienePrecio(p, cache);
  const como = new Map<string, Coincidencia>();
  const aFila = (grupo: GrupoBusqueda) => (p: ProductoLocal): ResultadoBusqueda => {
    const c = como.get(p.id);
    return { ...aResultado(p, cache), grupo, ...(c && c !== "exacto" ? { coincidencia: c } : {}) };
  };
  const conPrecio = (r: ResultadoBusqueda) => r.presentaciones.length > 0;
  if (!termino) {
    return cache.productos.filter(vendible).slice(0, limite).map(aFila("nombre")).filter(conPrecio);
  }
  // GTIN exacto primero (hot-path de tipeo del código).
  const porCodigo = cache.gtin.get(q.trim());
  const seed = porCodigo ? cache.productos.filter((p) => p.id === porCodigo.producto_id) : [];
  const tope = Math.min(limite, LIMITE_POR_GRUPO.nombre);
  const qp = plano(termino);
  const qs = sonido(termino);
  const elegidos = new Set<string>(seed.map((p) => p.id));
  const porNombre: ProductoLocal[] = seed.filter(vendible).slice(0, tope);
  const pasada = (cumple: (p: ProductoLocal) => boolean, c: Coincidencia) => {
    for (const p of cache.productos) {
      if (porNombre.length >= tope) return;
      if (elegidos.has(p.id) || !vendible(p) || !cumple(p)) continue;
      elegidos.add(p.id);
      como.set(p.id, c);
      porNombre.push(p);
    }
  };
  if (qp) {
    pasada((p) => (cache.nombrePlano.get(p.id) ?? "").includes(qp), "exacto");
    if (porNombre.length < MINIMO_ANTES_DE_TOLERAR && qs.length >= 3) pasada((p) => (cache.nombreSonido.get(p.id) ?? "").includes(qs), "sonido");
    if (porNombre.length < MINIMO_ANTES_DE_TOLERAR) pasada((p) => unaLetra(cache.nombrePalabras.get(p.id) ?? [], qs), "letra");
  }

  // Con 1 o 2 letras se está tipeando todavía: solo el nombre (si no, la lista se llena de ruido).
  if (termino.length < 3) return porNombre.map(aFila("nombre")).filter(conPrecio);
  const principios = new Set<string>();
  for (const p of porNombre) {
    const pa = cache.paNorm.get(p.id) ?? "";
    if (pa) principios.add(pa);
  }
  // Lo tipeado como principio activo: solo uno solo («paracetamol»), no una combinación que lo contenga
  // («paracetamol + cafeína» es otro medicamento).
  for (const p of cache.productos) {
    const pa = cache.paNorm.get(p.id) ?? "";
    if (!pa || esCombinacion(pa)) continue;
    // También por cómo suena: «amoxisilina» trae las marcas de amoxicilina.
    if (pa.startsWith(termino) || (qs.length >= 3 && (cache.paSonido.get(p.id) ?? "").startsWith(qs))) principios.add(pa);
  }
  const yaEstan = new Set(porNombre.map((p) => p.id));
  const similares: ProductoLocal[] = [];
  const genericos: ProductoLocal[] = [];
  if (principios.size > 0) {
    for (const p of cache.productos) {
      if (yaEstan.has(p.id)) continue;
      const pa = cache.paNorm.get(p.id) ?? "";
      if (!pa || !principios.has(pa) || !vendible(p)) continue;
      if (tipoEfectivo(p.tipo, p.nombre, p.principio_activo) === "generico") {
        if (genericos.length < LIMITE_POR_GRUPO.generico) genericos.push(p);
      } else if (similares.length < LIMITE_POR_GRUPO.similar) {
        similares.push(p);
      }
      if (genericos.length >= LIMITE_POR_GRUPO.generico && similares.length >= LIMITE_POR_GRUPO.similar) break;
    }
  }
  return [
    ...porNombre.map(aFila("nombre")),
    ...similares.map(aFila("similar")),
    ...genericos.map(aFila("generico")),
  ].filter(conPrecio);
}

// Qué presentación ofrece la tarjeta de sugerencia (Kevin, 16-oct-2026: «el blíster de 10 unidades está
// 2.50 soles … que la sugerencia ofrezca el blíster»). Antes ofrecía siempre la unidad suelta. Ahora: la
// presentación más chica de más de una unidad que tenga precio aquí y que el stock alcance; si no hay, la
// unidad base, como antes. Con el stock sin cargar (`null`) no se descarta nada.
export function presentacionSugerida(presentaciones: PresentacionVenta[], stock: number | null): PresentacionVenta | null {
  const alcanza = (p: PresentacionVenta) => stock == null || stock >= p.factor;
  const blister = presentaciones
    .filter((p) => p.factor > 1 && alcanza(p))
    .sort((a, b) => a.factor - b.factor)[0];
  return blister ?? presentaciones.find((x) => x.es_base) ?? presentaciones[0] ?? null;
}

export type Escaneo =
  | { tipo: "ok"; producto: ProductoVenta }
  | { tipo: "sin_precio"; nombre: string; presentacion: string }
  | { tipo: "no_esta" };

// Lo que pasa al escanear un código. El código apunta a SU presentación (Δ1: el de la caja ≠ el de la
// unidad). Si esa presentación no tiene precio en esta botica NO se cae a la unidad (Kevin, 30-sep:
// antes una caja x100 sin precio se cobraba como una unidad): se avisa y no se agrega nada. Solo un
// código que no dice presentación usa la base. Un producto sin NINGÚN precio aquí sigue como antes
// («no está»: el mostrador ofrece «Sí lo tengo»).
export function escanearEnCache(cache: Cache, gtin: string): Escaneo {
  const cod = cache.gtin.get(gtin.trim());
  if (!cod) return { tipo: "no_esta" };
  const producto = cache.productos.find((p) => p.id === cod.producto_id);
  if (!producto) return { tipo: "no_esta" };
  const r = aResultado(producto, cache);
  if (r.presentaciones.length === 0) return { tipo: "no_esta" };
  if (cod.presentacion_id) {
    const pres = r.presentaciones.find((x) => x.presentacion_id === cod.presentacion_id);
    if (pres) return { tipo: "ok", producto: aProductoVenta(r, pres, gtin.trim()) };
    const nombrePres = (cache.presPorProducto.get(producto.id) ?? []).find((x) => x.id === cod.presentacion_id)?.nombre;
    if (nombrePres) return { tipo: "sin_precio", nombre: producto.nombre, presentacion: nombrePres };
  }
  const base = r.presentaciones.find((x) => x.es_base) ?? r.presentaciones[0]!;
  return { tipo: "ok", producto: aProductoVenta(r, base, gtin.trim()) };
}

export function useCatalogoVenta(db: HuayruroDB): {
  buscar: (q: string, limite?: number) => ResultadoBusqueda[];
  porGtin: (gtin: string) => ProductoVenta | null;
  /** Escaneo en el mostrador: además del producto, dice si el código es de una caja sin precio aquí. */
  escanear: (gtin: string) => Escaneo;
  /** A4: resuelve el producto sugerido por una regla, en el blíster si tiene precio (ver `presentacionSugerida`). */
  porProductoId: (productoId: string) => ProductoVenta | null;
  /** A4: stock local por producto — `null` mientras la cache no cargó (ahí el motor no bloquea nada). */
  stockPorProducto: Record<string, number> | null;
  listo: boolean;
  total: number;
} {
  const datos = useLiveQuery(async () => {
    const [productos, presentaciones, precios, codigos, stock, acciones, umbral] = await Promise.all([
      db.productos.where("activo").equals(1).toArray(),
      db.presentaciones.toArray(),
      db.precios.toArray(),
      db.codigos.toArray(),
      db.stock_cache.toArray(),
      db.copiloto_acciones.toArray(),
      db.meta.get(META_UMBRAL),
    ]);
    return { productos, presentaciones, precios, codigos, stock, acciones, umbral: (umbral?.valor as UmbralBotica | undefined) ?? null };
  }, [db]);

  const cache = useMemo<Cache | null>(() => (datos ? armarCache(datos) : null), [datos]);

  return useMemo(() => {
    const listo = cache !== null;
    if (!cache) {
      return { buscar: () => [], porGtin: () => null, escanear: (): Escaneo => ({ tipo: "no_esta" }), porProductoId: () => null, stockPorProducto: null, listo: false, total: 0 };
    }

    const buscar = (q: string, limite = 50): ResultadoBusqueda[] => buscarEnCache(cache, q, limite);

    const escanear = (gtin: string): Escaneo => escanearEnCache(cache, gtin);
    const porGtin = (gtin: string): ProductoVenta | null => {
      const e = escanearEnCache(cache, gtin);
      return e.tipo === "ok" ? e.producto : null;
    };

    // Si el producto no tiene precio vigente en esta botica no se puede vender, y el motor de arriba
    // nunca llega a mostrarlo. La presentación la elige `presentacionSugerida`.
    const porProductoId = (productoId: string): ProductoVenta | null => {
      const producto = cache.productos.find((p) => p.id === productoId);
      if (!producto) return null;
      const r = aResultado(producto, cache);
      const pres = presentacionSugerida(r.presentaciones, cache.stock.get(productoId) ?? null);
      if (!pres) return null;
      // Sin GTIN a propósito: la sugerencia se agrega de un tap, no se escaneó ningún código.
      return aProductoVenta(r, pres, null);
    };

    const stockPorProducto = Object.fromEntries(cache.stock);

    return { buscar, porGtin, escanear, porProductoId, stockPorProducto, listo, total: cache.productos.length };
  }, [cache]);
}
